import React, { useState } from 'react';
import { Button, CircularProgress, Snackbar } from '@mui/material';
import { PictureAsPdfOutlined } from '@mui/icons-material';
import type { DetailLevel } from '../../../../support/aiEraReport/shared/detail-level';
import type { ReportConfig, ReportModel } from '../../../../support/reportView/types';

/**
 * "Download PDF" button for the generic report. It dynamically imports the
 * @react-pdf renderer only on click, so the (heavy) PDF engine never lands in
 * the main assessment bundle. It exports exactly what is on screen — the same
 * `config` composition + `model` data at the current detail level and role
 * framing — so the leave-behind matches the view it was generated from.
 */

type Props = {
  config: ReportConfig;
  model: ReportModel;
  level: DetailLevel;
  roleView: string;
  dark?: boolean;
  fileName?: string;
};

const DownloadReportButton: React.FC<Props> = ({ config, model, level, roleView, dark, fileName }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = async () => {
    setBusy(true);
    setError(null);
    try {
      const { downloadReportPdf } = await import('./download');
      await downloadReportPdf({
        config,
        model,
        level,
        roleView,
        orgName: (model.meta?.orgName as string) || undefined,
        generatedAt: (model.meta?.generatedAt as string) || undefined,
        fileName,
      });
    } catch (e) {
      setError('Could not generate the PDF.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={handleClick}
        disabled={busy}
        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfOutlined />}
        sx={dark ? { color: '#9fb3cc', borderColor: 'rgba(159,179,204,0.4)' } : undefined}
      >
        {busy ? 'Preparing…' : 'Download PDF'}
      </Button>
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        message={error}
      />
    </>
  );
};

export default DownloadReportButton;
